import { LockClosedIcon } from "@heroicons/react/20/solid"

import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"

import {
  formatPersonPhone,
  personName,
} from "../BatteryOnlyProposal/person-display"
import {
  SetupDetailSection,
  type SetupDetailRow,
} from "../ProposalSetup/SetupDetailSection"

/** A field always keeps its label; only the value falls back. */
const NOT_PROVIDED = `—`

const ENERGY_COMMUNITY_NOTE = `Homes in energy communities may qualify for additional discounts when selecting Concert or Participate in funding.`

const present = (value: string | null | undefined): string =>
  value?.trim() || NOT_PROVIDED

/** An answer the read didn't carry (a gateway older than the client) stays
 * unstated rather than reading as No. */
const yesNo = (value: boolean | undefined): string =>
  value == null ? NOT_PROVIDED : value ? `Yes` : `No`

const contactRows = (
  homeowner: SolarProposalDto[`homeowner`],
): readonly SetupDetailRow[] => [
  { label: `Full name`, value: present(homeowner && personName(homeowner, ``)) },
  { label: `Email`, value: present(homeowner?.email) },
  {
    label: `Phone`,
    value: present(formatPersonPhone(homeowner?.phone ?? null)),
  },
]

/** The proposal's display address, as V1 prefers it. A blank field reads as
 * not provided rather than falling back to the Site's placeholder defaults. */
const addressRows = (
  address: SolarProposalDto[`address`],
): readonly SetupDetailRow[] => [
  { label: `Street`, value: present(address.street) },
  { label: `City`, value: present(address.city) },
  { label: `State`, value: present(address.state) },
  { label: `ZIP`, value: present(address.postal) },
]

/**
 * Site Information: the contact, service address and energy-community status
 * the Solar Proposal was built with. Read-only, from the header read the page
 * already holds.
 */
export const SolarSiteStep = ({
  proposal,
}: {
  proposal: SolarProposalDto
}): JSX.Element => (
  <div className="flex flex-col gap-6" data-testid="solar-site-step">
    <SetupDetailSection title="Contact" rows={contactRows(proposal.homeowner)} />
    <SetupDetailSection
      title="Service Address"
      rows={addressRows(proposal.address)}
    />
    <SetupDetailSection
      title="Energy Community"
      rows={[
        {
          label: `Home is in an energy community`,
          value: yesNo(proposal.isEnergyCommunity),
        },
      ]}
      note={ENERGY_COMMUNITY_NOTE}
    />
    <p className="flex items-center gap-2 border-t border-border pt-4 text-sm font-semibold text-muted">
      <LockClosedIcon aria-hidden="true" className="size-4 shrink-0" />
      Homeowner and site details are read only
    </p>
  </div>
)
